import { XcoreConfigValue } from "../config";
import type { RequestContext } from "@robe/core-api/options";
import { XcoreCore } from "../XcoreCore/index";

export class XcoreConfig {
  /** API URL in format `https://robe.cz` */
  public get apiUrl() {
    return this._value.apiUrl;
  }
  public get defaultLocale() {
    return this.value.defaultLocale;
  }
  /** Key is locale and value is object of type `LocalesValue` */
  public get locales() {
    return this._value.locales;
  }
  /** Domains that should not be shown in web domain switcher */
  public get secondaryDomains() {
    return this._value.secondaryDomains;
  }
  public get revalidate() {
    return this._value.revalidate;
  }
  public get admin() {
    return this._value.admin;
  }
  public get value() {
    return this._value;
  }
  public get env() {
    return this._value.env;
  }

  private _value: XcoreConfigValue;

  constructor(value: XcoreConfigValue) {
    this._value = value;
    this.getResolvedDomains = this.getResolvedDomains.bind(this);
  }

  /**
   * Key is domain and value is locale.
   * If no domains are set for environment,
   * domains object will be created from current host and available locales.
   * */
  public getResolvedDomains(host = "") {
    /** Object where key is domain and value is locale */
    let resolvedDomains: Record<string, string> = {};
    const localesKeys = Object.keys(this._value.locales);

    // Loop through all locales and its domains if there are any specified
    localesKeys.forEach((locale) => {
      const localeDomains = this._value.locales[locale]?.domains;

      if (localeDomains && localeDomains.length > 0) {
        localeDomains.forEach(({ domain }) => {
          resolvedDomains = {
            ...resolvedDomains,
            [domain]: locale,
          };
        });
      }
    });

    if (Object.keys(resolvedDomains).length === 0) {
      return Object.fromEntries(
        Object.keys(this.locales).map((locale) => [`${host}/${locale === this.defaultLocale ? "" : locale}`, locale])
      );
    }

    return resolvedDomains;
  }

  /**
   * Used in middleware and FE EPs. Provide current locale to load core instance.
   * Provide additional ctx config (e.g. in middleware)
   *  */
  public getCoreInstance(ctxOrCurrentLocale: RequestContextInput | string, ctx?: Omit<Partial<RequestContext>, "locales">): XcoreCore {
    if (typeof ctxOrCurrentLocale === "string") {
      return new XcoreCore({
        ...ctx,
        locales: this.locales,
        currentLocale: ctxOrCurrentLocale,
        apiUrl: this.apiUrl,
      });
    }

    return new XcoreCore({
      ...ctxOrCurrentLocale,
      currentLocale: ctxOrCurrentLocale?.currentLocale ?? this.defaultLocale,
      apiUrl: this.apiUrl,
    });
  }
}

type RequestContextInput = Pick<RequestContext, "locales"> & Partial<Omit<RequestContext, "locales">>;
